/**
 * COHORT START DIALLER — header button + floating call cockpit for the REPEAT
 * (cohort) team, who have NO My Chats access (Shailendra 2026-08-27).
 *
 * New-rev agents start the dialer from inside My Chats (chat-as-context +
 * DialerCallPane). Cohort agents call their OWN already-assigned buyers to drive
 * reorders — they don't need the WhatsApp chat pane. So this puts Start Dialler in
 * the top bar and renders the SAME DialerCallPane wrap-up cockpit as a floating
 * panel (no chat). Reuses the hardened voizDialer engine end-to-end.
 *
 * Shown ONLY when GET /voice/dialer/is-cohort → isCohort. Starts from
 * GET /voice/dialer/my-cohort-list (the agent's ranked cohort book).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Button, Tooltip, Snackbar, Box, Paper, Typography, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PhoneForwardedIcon from '@material-ui/icons/PhoneForwarded';
import AddIcCallIcon from '@material-ui/icons/AddIcCall';
import CloseIcon from '@material-ui/icons/Close';
import DialerCallPane from '../../../menu-items/Dashboard/DialerCallPane';
import usePredictiveStatus from '../../../hooks/usePredictiveStatus';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();

export default function CohortStartDialer() {
  const [isCohort, setIsCohort] = useState(false);
  // admins.predictiveDialer:true agents run PredictiveCohortDialer instead —
  // this progressive Start Dialler/Add Number pair is meaningless for them
  // (they never hold the underlying voizDialer queue), so both stay hidden.
  // Read from the shared poller (usePredictiveStatus) instead of this
  // component's own one-off fetch — same endpoint PredictiveCohortDialer/
  // PredictiveSessionButton/MyChats all read, now polled once, not 4x.
  const [predictiveSt] = usePredictiveStatus();
  const predictiveEnabled = !!predictiveSt?.enabled;
  const [dialer, setDialer] = useState(null);      // live queue status (null = inactive)
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const pollRef = useRef(null);
  const stoppedRef = useRef(false);

  // gate: only cohort agents see this button
  useEffect(() => {
    axios.get(`${AI_PATH}/api/ai/voice/dialer/is-cohort`)
      .then((r) => setIsCohort(!!r.data?.isCohort))
      .catch(() => setIsCohort(false));
  }, []);

  const poll = useCallback(async () => {
    if (stoppedRef.current) return;
    try {
      const r = await axios.get(`${AI_PATH}/api/ai/voice/dialer/status`);
      const st = r.data;
      const hasQueue = st && Array.isArray(st.queue) && st.queue.length > 0;
      const hasPending = hasQueue && st.queue.some((it) => it.status === 'pending');
      setDialer((st && (st.active || hasPending)) ? st : null);
      if (!st?.active && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (e) { /* ignore transient */ }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    poll();
    pollRef.current = setInterval(poll, 15000);   // heartbeat; actions refresh on demand
  }, [poll]);

  useEffect(() => {
    if (!isCohort) return undefined;
    poll();   // resume if a queue is already live (page reload mid-run)
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [isCohort, poll]);

  const refreshNow = useCallback(() => { setTimeout(poll, 400); setTimeout(poll, 1200); }, [poll]);

  // Other screens (e.g. the Repeat drill-down's per-lead Call button) queue a
  // number or start the dialer directly via the API, then fire this event so
  // the cockpit here — mounted separately in the header — picks it up right
  // away instead of waiting for the next 15s heartbeat.
  useEffect(() => {
    if (!isCohort) return undefined;
    const onExternalRefresh = () => { stoppedRef.current = false; startPolling(); refreshNow(); };
    window.addEventListener('cohort-dialer-refresh', onExternalRefresh);
    return () => window.removeEventListener('cohort-dialer-refresh', onExternalRefresh);
  }, [isCohort, startPolling, refreshNow]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${AI_PATH}/api/ai/voice/dialer/my-cohort-list`);
      const leads = r.data?.leads || [];
      if (!leads.length) { setToast({ open: true, severity: 'error', message: 'No cohort buyers to dial for you today.' }); setBusy(false); return; }
      const s = await axios.post(`${AI_PATH}/api/ai/voice/dialer/start`, { leads });
      if (s.data?.success) {
        stoppedRef.current = false;
        // The queue now holds a ROLLING 5, topped up from the live book after
        // every call (cohortRefill.js) — so "1 of 5" would badly understate the
        // day. Show the book size the route reports, and the due call-backs.
        const bookTotal = r.data?.total || s.data.total;
        const dueTotal = r.data?.dueTotal || 0;
        const duePart = dueTotal ? ` ${dueTotal} call-back${dueTotal > 1 ? 's' : ''} due first.` : '';
        setToast({ open: true, severity: 'success', message: `Dialer started — calling ${s.data.phone}. ${bookTotal} buyers in your book.${duePart} Wear your headset.` });
        setDialer({ active: true });
        startPolling();
      } else {
        setToast({ open: true, severity: 'error', message: s.data?.message || 'Could not start dialer.' });
      }
    } catch (e) {
      const msg = e.response?.data?.code === 'SOFTPHONE_OFFLINE'
        ? 'Log into the VOIZ softphone first, then start the dialer.'
        : (e.response?.data?.message || e.message);
      setToast({ open: true, severity: 'error', message: msg });
    } finally { setBusy(false); }
  };

  const stop = async () => {
    stoppedRef.current = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setDialer(null);
    try { await axios.post(`${AI_PATH}/api/ai/voice/dialer/stop`, {}); } catch (e) { /* ignore */ }
    setTimeout(() => { stoppedRef.current = false; }, 4000);
  };

  const dialerActive = !!dialer?.active;

  // "+ Add Number" — same guardrail as retail's: only a lead ALREADY assigned
  // to this agent (their cohort book) can be queued. Backend enforces it
  // (voizDialer.addNumber checks leads.salesExecutiveId + cohortAssignedAt);
  // this is just the entry point.
  const [addOpen, setAddOpen] = useState(false);
  const [addVal, setAddVal] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const submitAddNumber = async () => {
    const p = addVal.replace(/\D/g, '').slice(-10);
    if (p.length !== 10) { setToast({ open: true, severity: 'error', message: 'Enter a valid 10-digit number.' }); return; }
    setAddBusy(true);
    try {
      const r = await axios.post(`${AI_PATH}/api/ai/voice/dialer/add-number`, { phone: p });
      if (r.data?.success) {
        setToast({ open: true, severity: 'success', message: r.data.message || 'Added — will be dialled next.' });
        setAddOpen(false); setAddVal('');
        refreshNow();
      } else {
        setToast({ open: true, severity: 'error', message: r.data?.message || 'Could not add that number.' });
      }
    } catch (e) {
      setToast({ open: true, severity: 'error', message: e.response?.data?.message || e.message });
    } finally { setAddBusy(false); }
  };

  if (!isCohort) return null;

  return (
    <>
      {/* predictiveDialer:true agents don't get Start (they run
          PredictiveCohortDialer instead) — but if their progressive queue is
          already active (started before the flag flipped or before this gate
          shipped), Stop must stay visible so they aren't stranded. */}
      {(!predictiveEnabled || dialerActive) && (
        <Tooltip title="Start calling your cohort buyers — ranked by reorder intent, one after another.">
          <span>
            <Button
              onClick={() => (dialer ? stop() : start())}
              disabled={busy}
              variant="outlined"
              startIcon={<PhoneForwardedIcon />}
              style={{
                marginRight: 12, textTransform: 'none', fontWeight: 700, fontSize: 13,
                padding: '6px 14px', borderRadius: 8,
                color: dialer ? '#b71c1c' : '#1565c0',
                borderColor: dialer ? '#ef9a9a' : '#90caf9',
                background: dialer ? '#fdecea' : '#e8f2fd',
                whiteSpace: 'nowrap',
              }}
            >
              {/* The queue is a rolling 5, topped up after every call, so
                  "cursor / queue length" would tick 3/5 then 6/10 and read like the
                  target kept moving. Show calls DONE — that number only goes up. */}
              {dialer ? `Stop Dialler (${dialer.cursor || 0} done)` : 'Start Dialler'}
            </Button>
          </span>
        </Tooltip>
      )}

      {(!predictiveEnabled || dialerActive) && (
        <Tooltip title="Add one of your own cohort buyers to be dialled next">
          <span>
            <Button
              onClick={() => setAddOpen(true)}
              variant="outlined"
              startIcon={<AddIcCallIcon />}
              style={{
                marginRight: 12, textTransform: 'none', fontWeight: 700, fontSize: 13,
                padding: '6px 14px', borderRadius: 8,
                color: '#1565c0', borderColor: '#90caf9', background: '#e8f2fd', whiteSpace: 'nowrap',
              }}
            >
              Add Number
            </Button>
          </span>
        </Tooltip>
      )}

      <Dialog open={addOpen} onClose={() => !addBusy && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a number to dial next</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
            Only works for a buyer already assigned to you as a cohort lead.
          </Typography>
          <TextField
            autoFocus fullWidth label="Phone number" placeholder="e.g. 9876543210"
            value={addVal} onChange={(e) => setAddVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !addBusy) submitAddNumber(); }}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addBusy}>Cancel</Button>
          <Button onClick={submitAddNumber} disabled={addBusy || addVal.replace(/\D/g, '').length !== 10} variant="contained" color="primary">
            {addBusy ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating call cockpit — the SAME DialerCallPane as new-rev, minus the
          WhatsApp chat pane. Fixed bottom-right so it overlays whatever page the
          cohort agent is on. zIndex above MUI's default modal layer (1300) so it
          stays visible even when a report/drill-down dialog is open underneath. */}
      {dialerActive && (
        <Paper
          elevation={8}
          style={{
            position: 'fixed', right: 16, bottom: 16, zIndex: 1400,
            width: 300, maxHeight: '80vh', overflow: 'hidden', borderRadius: 10,
            display: 'flex', flexDirection: 'column',
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#eef4fd', borderBottom: '1px solid #e0e0e0' }}>
            <Typography style={{ fontWeight: 800, fontSize: 12, color: '#1565c0' }}>Cohort Dialer</Typography>
            <IconButton size="small" onClick={stop} title="Stop dialler"><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box style={{ overflowY: 'auto' }}>
            <DialerCallPane
              dialer={dialer}
              cohort
              onEnded={refreshNow}
              onSubmitted={refreshNow}
              onResume={refreshNow}
              onDialNext={refreshNow}
              onHold={refreshNow}
              onRefreshChat={() => {}}   /* no chat pane for cohort */
              onStop={stop}
              showBuyerInfo
            />
          </Box>
        </Paper>
      )}

      <Snackbar open={toast.open} autoHideDuration={7000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
}
